import { createStore } from 'vuex'
import instance from '@/api/index'
import { resolveMediaUrl } from '@/utils/media'

// 角色对应的可访问路由
const roleRoutes = {
  admin: ['/', '/member', '/coach', '/course', '/appointment', '/equipment', '/checkin', '/admin'],
  coach: ['/', '/course', '/appointment', '/checkin', '/coach-members', '/coach-courses', '/coach-profile'],
  member: ['/', '/appointment', '/checkin', '/profile', '/member-courses', '/member-coaches', '/member-equipment'],
}

// 从 localStorage 恢复登录状态
const savedUser = (() => {
  try {
    const raw = localStorage.getItem('gym_user')
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
})()

const savedBranding = (() => {
  try {
    const raw = localStorage.getItem('gym_branding')
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
})()

export default createStore({
  state: {
    user: savedUser,          // { username, role, token, avatar, memberId? }
    token: savedUser?.token || null,
    branding: {
      gymName: savedBranding?.gymName || 'LukaGYM 管理系统',
      gymLogo: savedBranding?.gymLogo || '',
    },
  },

  getters: {
    isLoggedIn: (state) => !!state.token && !!state.user,
    currentUser: (state) => state.user,
    userRole: (state) => state.user?.role || null,
    userName: (state) => state.user?.username || '',
    userAvatar: (state) => resolveMediaUrl(state.user?.avatar || ''),

    /** 业务主键：会员为 memberId，教练为 coachId，管理员可能为空 */
    typeId: (state) => state.user?.typeId ?? null,

    gymName: (state) => state.branding?.gymName || 'LukaGYM 管理系统',
    gymLogo: (state) => resolveMediaUrl(state.branding?.gymLogo || ''),

    // 根据角色返回允许访问的路由列表
    allowedRoutes: (state) => {
      const role = state.user?.role
      return roleRoutes[role] || []
    },

    // 角色的中文显示名
    roleLabel: (state) => {
      const map = { admin: '管理员', coach: '教练', member: '会员' }
      return map[state.user?.role] || '用户'
    },

    // 根据角色返回可见的导航菜单项
    navItems: (state) => {
      const role = state.user?.role
      const all = [
        { index: '/', icon: 'DataAnalysis', title: '数据概览', roles: ['admin', 'coach', 'member'] },
        { index: '/member', icon: 'UserFilled', title: '会员管理', roles: ['admin'] },
        { index: '/coach', icon: 'User', title: '教练管理', roles: ['admin'] },
        { index: '/course', icon: 'Notebook', title: '课程管理', roles: ['admin'] },
        { index: '/coach-members', icon: 'UserFilled', title: '我的学员', roles: ['coach'] },
        { index: '/coach-courses', icon: 'Notebook', title: '我的课程', roles: ['coach'] },
        { index: '/coach-profile', icon: 'User', title: '个人中心', roles: ['coach'] },
        { index: '/member-courses', icon: 'Notebook', title: '课程浏览', roles: ['member'] },
        { index: '/member-coaches', icon: 'User', title: '教练团队', roles: ['member'] },
        { index: '/member-equipment', icon: 'Tools', title: '器材查看', roles: ['member'] },
        { index: '/appointment', icon: 'Calendar', title: '预约管理', roles: ['admin', 'coach', 'member'] },
        { index: '/equipment', icon: 'Tools', title: '器材管理', roles: ['admin'] },
        { index: '/checkin', icon: 'Stopwatch', title: '打卡记录', roles: ['admin', 'coach', 'member'] },
        { index: '/profile', icon: 'User', title: '个人中心', roles: ['member'] },
        { index: '/admin', icon: 'Setting', title: '系统设置', roles: ['admin'] },
        
      ]
      if (!role) return all
      return all.filter((item) => item.roles.includes(role))
    },
  },

  mutations: {
    SET_USER(state, payload) {
      state.user = payload
      state.token = payload?.token || null
      if (payload) {
        localStorage.setItem('gym_user', JSON.stringify(payload))
      } else {
        localStorage.removeItem('gym_user')
      }
    },
    CLEAR_USER(state) {
      state.user = null
      state.token = null
      localStorage.removeItem('gym_user')
    },
    SET_BRANDING(state, payload) {
      state.branding = {
        gymName: payload.gymName || state.branding.gymName,
        gymLogo: payload.gymLogo != null ? payload.gymLogo : state.branding.gymLogo,
      }
      localStorage.setItem('gym_branding', JSON.stringify(state.branding))
    },
    UPDATE_USER_FIELDS(state, payload) {
      if (!state.user) return
      state.user = { ...state.user, ...payload }
      localStorage.setItem('gym_user', JSON.stringify(state.user))
    },
  },

  actions: {
    login({ commit }, userData) {
      commit('SET_USER', userData)
    },
    logout({ commit }) {
      commit('CLEAR_USER')
    },
    async fetchBranding({ commit }) {
      try {
        const res = await instance.get('/system/settings/public')
        if (res.data && res.data.code === 200 && res.data.data) {
          const d = res.data.data
          commit('SET_BRANDING', { gymName: d.gymName, gymLogo: d.gymLogo || '' })
        }
      } catch {
        /* 网络失败时沿用缓存或默认 */
      }
    },
  },

  modules: {},
})
